/**
 * 入力画面の発話区間（Issue #461）: 動画の位置の目安として前後の区間の頭へ飛ぶ・今の発話を字幕に出す。
 * 発話区間のレビュー（採否の切り替え・文の直し・LLM の提案の適用 / 却下。Issue #998 でシミュレータの画面から移した）の書き込みもここ。
 * 画面は発話タブ（`SpeechTab.js`）。**常に見えている所（再生バー・情報帯）には採否を書き換える操作を置かない**（押し間違いで黙って反転する。Issue #290）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/api';
import { proposalTargets, seekableSegments, segmentIndexAt, speechLabel } from '../../utils/speechSegments';

export { prevSpeechMs, nextSpeechMs } from '../../utils/speechSegments';

const listOf = (res) => (Array.isArray(res) ? res : (res?.data ?? res?.segments ?? []));
const failed = (res) => !res || (res.status != null && res.status !== 'success') || !!res.error;
const reason = (res) => res?.error ?? res?.status ?? '応答がありません';

/**
 * 発話区間（開始時刻順・シークの対象だけ）と、その書き込み。読めなければ空。
 * 書き込みは先に画面へ反映し、失敗したら読み直して `error` に理由を残す（`clearError` で消す）。
 * サーバーの action は /duel の GetSpeechSegments / SetSpeechSegmentEnabled / SetSpeechSegmentText / Apply・DismissSpeechSegmentProposal（`duel_id` は試合の game_uuid）
 */
export function useSpeechSegments(gameUuid) {
  const [segments, setSegments] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const reload = useCallback(async () => {
    if (!gameUuid) return;
    try {
      const res = await api.getSpeechSegments(gameUuid);
      if (alive.current) setSegments(seekableSegments(listOf(res)));
    } catch {
      if (alive.current) setSegments([]);
    }
  }, [gameUuid]);
  useEffect(() => { setSegments([]); reload(); }, [reload]);

  const patch = (id, fields) => setSegments((prev) => prev.map((s) => (s.id === id ? { ...s, ...fields } : s)));
  /** 1 件書く。失敗したら読み直して理由を残す */
  const write = useCallback(async (call, label) => {
    setError(null);
    try {
      const res = await call();
      if (failed(res)) { setError(`${label}に失敗しました: ${reason(res)}`); await reload(); return false; }
      return true;
    } catch (e) {
      setError(`${label}に失敗しました: ${e?.message ?? e}`);
      await reload();
      return false;
    }
  }, [reload]);

  const setEnabled = useCallback((id, enabled) => {
    patch(id, { enabled });
    return write(() => api.setSpeechSegmentEnabled(gameUuid, id, enabled), enabled ? '採用' : 'カット');
  }, [gameUuid, write]);
  const setText = useCallback((id, text) => {
    patch(id, { text });
    return write(() => api.setSpeechSegmentText(gameUuid, id, text), '文の直し');
  }, [gameUuid, write]);
  // 提案はサーバーが文を差し替えて提案を消すので、書いたら読み直す
  const applyProposal = useCallback(async (id) => { const ok = await write(() => api.applySpeechSegmentProposal(gameUuid, id), '提案の適用'); if (ok) await reload(); return ok; }, [gameUuid, write, reload]);
  const dismissProposal = useCallback(async (id) => { const ok = await write(() => api.dismissSpeechSegmentProposal(gameUuid, id), '提案の却下'); if (ok) await reload(); return ok; }, [gameUuid, write, reload]);
  /** 提案を全部（一部が落ちても成功した分はサーバーに入っているので、必ず読み直す） */
  const bulk = useCallback(async (call, label) => {
    const targets = proposalTargets(segments);
    if (!targets.length) return;
    setBusy(true); setError(null);
    try {
      const results = await Promise.allSettled(targets.map((s) => call(gameUuid, s.id)));
      const n = results.filter((r) => r.status === 'rejected' || failed(r.value)).length;
      if (n > 0) setError(`${n} 件の${label}に失敗しました`);
    } finally {
      await reload();
      if (alive.current) setBusy(false);
    }
  }, [segments, gameUuid, reload]);
  const applyAll = useCallback(() => bulk((g, id) => api.applySpeechSegmentProposal(g, id), '適用'), [bulk]);
  const dismissAll = useCallback(() => bulk((g, id) => api.dismissSpeechSegmentProposal(g, id), '却下'), [bulk]);

  return { segments, error, busy, clearError: () => setError(null), reload, setEnabled, setText, applyProposal, dismissProposal, applyAll, dismissAll };
}

/** ms に居る区間の添字（開始済みのうち一番新しいもの） */
export const speechIndexAt = (sorted, ms) => segmentIndexAt(sorted, ms);

/** ms に出す字幕（採用の発話で、その時刻に喋っているもの。複数なら全部）。{ speaker, text }[] */
export function subtitlesAt(sorted, ms) {
  return sorted
    .filter((s) => s.enabled !== false && s.start_ms <= ms && ms < s.end_ms && (s.text || '').trim())
    .map((s) => ({ id: s.id ?? `${s.speaker}:${s.start_ms}`, speaker: speechLabel(s), text: s.text }));
}
